import React, { useRef, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { ProductListProps } from '../../../types/common';
import CarouselHeader from '../../carouselHeader/component';
import { RECOMMENDATION_BANNER_SLIDE_TYPE } from '../../recommendationBannerCarousel/component';
import { GridContainerStyle, GridSlideWrapper, ListContainer, LoadingContainer } from '../style';

const ProductListCarousel: React.FC<ProductListProps> = (props) => {
  const {
    slides,
    header,
    title,
    hasMore,
    fetchMoreData,
    shouldFetch,
    onCarouselScroll,
    onHeightChange,
    isLoading,
    resetScrollKey,
    isHorizonLayout,
  } = props;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const previousResetScrollKeyRef = useRef(resetScrollKey);
  const horizonLayoutRef = useRef({ isHorizonLayout, onHeightChange });

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && hasMore && shouldFetch && !isLoading) {
          fetchMoreData?.();
          onCarouselScroll?.();
        }
      },
      {
        root: null,
        threshold: 0.5,
      },
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [shouldFetch, hasMore, fetchMoreData, onCarouselScroll, isLoading]);

  useEffect(() => {
    horizonLayoutRef.current = { isHorizonLayout, onHeightChange };
  }, [isHorizonLayout, onHeightChange]);

  useEffect(() => {
    if (!resetScrollKey || previousResetScrollKeyRef.current === resetScrollKey) {
      previousResetScrollKeyRef.current = resetScrollKey;
      return;
    }

    previousResetScrollKeyRef.current = resetScrollKey;

    if (!listContainerRef.current) return;

    listContainerRef.current.scrollTo({
      top: 0,
      behavior: 'auto',
    });

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });

    const { isHorizonLayout: shouldNotifyHeightChange, onHeightChange: notifyHeightChange } = horizonLayoutRef.current;

    if (shouldNotifyHeightChange) {
      const animationFrameId = window.requestAnimationFrame(() => {
        notifyHeightChange?.();
      });
      const timers = [100, 300, 600].map((delay) => window.setTimeout(() => notifyHeightChange?.(), delay));

      return () => {
        window.cancelAnimationFrame(animationFrameId);
        timers.forEach((timer) => window.clearTimeout(timer));
      };
    }
  }, [resetScrollKey]);

  useEffect(() => {
    if (!isHorizonLayout) return;

    const animationFrameId = window.requestAnimationFrame(() => {
      onHeightChange?.();
    });
    const timers = [100, 300, 600].map((delay) => window.setTimeout(() => onHeightChange?.(), delay));

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [slides.length, isHorizonLayout, onHeightChange]);

  return (
    <>
      {header ? (
        <CarouselHeader title={header.title}>{header.children}</CarouselHeader>
      ) : (
        title && <CarouselHeader titleName={title} newHeader={true} />
      )}
      <ListContainer
        ref={listContainerRef}
        data-testid="product-list-container"
        id="scrollableDiv"
        isHorizonLayout={isHorizonLayout}
      >
        <GridContainerStyle data-testid="product-grid">
          {slides.map((slide, index) => {
            const isRecommendationBanner =
              React.isValidElement<{ slideType?: string }>(slide) &&
              slide.props.slideType === RECOMMENDATION_BANNER_SLIDE_TYPE;

            return (
              <GridSlideWrapper key={index} isWide={isRecommendationBanner}>
                {slide}
              </GridSlideWrapper>
            );
          })}
        </GridContainerStyle>
        <div ref={loadMoreRef} style={{ height: 20 }} data-testid="load-more-trigger" />
        {isLoading && (
          <LoadingContainer>
            <CircularProgress size={24} />
          </LoadingContainer>
        )}
      </ListContainer>
    </>
  );
};

export default ProductListCarousel;
